import React from 'react';
import { ArrowRight, Upload, FileText, Loader2 } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { PIPELINE_STAGE_LABELS } from './pipelineStages';
import { PipelineTransition } from './pipelineTransitions';
import { PlaquetteEmailFields, PlaquetteEmail } from './PlaquetteEmailFields';
import { moduleApi } from '../../lib/api';
import { DevisEmailFields, DevisEmail } from './DevisEmailFields';

/**
 * La modale qui fait avancer une étape — et qui REFUSE de valider tant que ce que l'étape exige n'est pas
 * fourni. Le bouton de confirmation reste éteint sans le document ou sans les champs obligatoires.
 *
 * Ce blocage est un confort, pas une sécurité : c'est le serveur qui refuse pour de bon (voir
 * PIPELINE_TRANSITIONS dans functions/api/[[path]].js). Se fier au bouton grisé pour garantir qu'un devis
 * signé existe serait exactement la faille que le cadre interdit.
 */
export function PipelineActionModal({ label, transition, saving, error, destinataire, destinataireEstReferent, onCancel, onConfirm }: {
    label: string;
    transition: PipelineTransition;
    saving: boolean;
    error: string;
    /** Adresse réelle de l'envoi, affichée avant de cliquer : référent s'il en a une, entreprise sinon. */
    destinataire?: string;
    destinataireEstReferent?: boolean;
    onCancel: () => void;
    onConfirm: (payload: Record<string, any>) => void;
}) {
    const [file, setFile] = React.useState<File | null>(null);
    const [values, setValues] = React.useState<Record<string, string>>({});
    const [reading, setReading] = React.useState(false);
    // Un document trop lourd est refusé DÈS LE CHOIX du fichier, pas après un téléversement de plusieurs
    // Mo suivi d'un refus du serveur. La base n'est plus la limite (les documents de privatisation sont
    // stockés en morceaux depuis le 30/08) : ce qui reste limité, c'est la boîte mail du destinataire, qui
    // refuse la pièce jointe au-delà d'une dizaine de Mo.
    const [fileError, setFileError] = React.useState('');
    // L'envoi de la plaquette est la seule étape qui parle au client : elle porte son propre message et sa
    // Démarche de destination. Coché par défaut — envoyer la plaquette EST le geste de cette étape ;
    // décocher sert au cas où elle a déjà été envoyée depuis une messagerie.
    const [courriel, setCourriel] = React.useState<PlaquetteEmail>({
        send_email: true, email_subject: '', email_message: '', demarche_template_id: '', demarche_template_ids_suivants: [],
    });
    const etapePlaquette = transition.documentKind === 'plaquette';

    // Objet, message et Demarche repris du reglage de Parametres > Notifications. Ils partaient vides a
    // chaque dossier : les textes gris de l'ecran n'etaient que des exemples, pas des valeurs, et le
    // commercial retapait tout au telephone. Demande d'Emeni (2026-08-29). Le texte reste modifiable ici
    // pour un client en particulier — le reglage donne le point de depart, pas le dernier mot.
    React.useEffect(() => {
        if (!etapePlaquette) return;
        let annule = false;
        moduleApi.getClientNotificationSettings()
            .then((s: any) => {
                if (annule || !s) return;
                setCourriel((prev) => ({
                    ...prev,
                    // Ne jamais ecraser ce qui a deja ete saisi : le reglage arrive apres le premier rendu.
                    email_subject: prev.email_subject || s.plaquette_send_subject || '',
                    email_message: prev.email_message || s.plaquette_send_body || '',
                    demarche_template_id: prev.demarche_template_id || s.plaquette_default_template_id || '',
                }));
            })
            .catch(() => {});
        return () => { annule = true; };
    }, [etapePlaquette]);

    // L'envoi du devis est la seconde étape qui parle au client. Coché par défaut, comme la plaquette :
    // envoyer le devis EST le geste de cette étape. Décocher sert au cas où il est déjà parti d'une
    // messagerie — le dossier le conserve alors sans lien de signature, et le signé se téléversera à la
    // main comme avant.
    const [courrielDevis, setCourrielDevis] = React.useState<DevisEmail>({
        send_email: true, email_subject: '', email_message: '',
    });
    const etapeDevis = transition.documentKind === 'devis';

    const champs = transition.requiresFields || [];
    const champsManquants = champs.some((f) => !(values[f.name] || '').trim());
    const documentManquant = transition.requiresDocument && !file;
    // Envoyer sans avoir choisi la Démarche produirait un email dont le bouton ne mène nulle part.
    const demarcheManquante = etapePlaquette && courriel.send_email && !courriel.demarche_template_id;
    const bloque = champsManquants || documentManquant || demarcheManquante || !!fileError || saving || reading;

    const confirmer = async () => {
        const payload: Record<string, any> = { to_stage: transition.nextStage };
        // Le serveur distingue « accompli » de « passé » : sans ce drapeau, il horodaterait une étape qui
        // n'a pas eu lieu, et le journal raconterait le contraire de la vérité.
        if (transition.skip) payload.skip = true;
        champs.forEach((f) => { payload[f.name] = values[f.name] || ''; });
        if (etapePlaquette) Object.assign(payload, courriel);
        if (etapeDevis) Object.assign(payload, courrielDevis);
        if (file) {
            setReading(true);
            try {
                // Lu en base64 côté navigateur, comme les autres dépôts de documents de l'application —
                // il n'y a pas de stockage objet dans ce projet (R2 écarté, décision d'Emeni 2026-08-26).
                const base64 = await new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(String(reader.result || '').split(',')[1] || '');
                    reader.onerror = () => reject(new Error('Lecture du fichier impossible.'));
                    reader.readAsDataURL(file);
                });
                payload.document_base64 = base64;
                payload.document_file_name = file.name;
                payload.document_mime_type = file.type || 'application/pdf';
            } finally {
                setReading(false);
            }
        }
        onConfirm(payload);
    };

    return (
        <Modal open onClose={onCancel} title={transition.actionLabel} icon={<ArrowRight size={16} />} maxWidth={etapePlaquette || etapeDevis ? "max-w-lg" : "max-w-md"}
            footer={
                <>
                    <Button variant="secondary" onClick={onCancel} disabled={saving}>Annuler</Button>
                    <Button variant="primary" onClick={confirmer} disabled={bloque}>
                        {saving || reading ? <Loader2 size={13} className="animate-spin" /> : null}
                        {saving ? 'Enregistrement...' : reading ? 'Lecture du fichier...' : 'Confirmer'}
                    </Button>
                </>
            }>
            <div className="space-y-4">
                <p className="text-xs text-content-muted">
                    <span className="font-bold text-content">{label}</span> passe au stade{' '}
                    <span className="font-bold text-brand-600 dark:text-brand-400">{PIPELINE_STAGE_LABELS[transition.nextStage]}</span>.
                </p>
                {transition.hint && (
                    <p className="text-[11px] text-content-muted leading-relaxed p-2.5 rounded-xl bg-surface-soft border border-border">
                        {transition.hint}
                    </p>
                )}

                {champs.map((f) => (
                    <div key={f.name} className="space-y-1">
                        <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">{f.label}</label>
                        <input type={f.type === 'number' ? 'number' : 'text'} value={values[f.name] || ''}
                            onChange={(e) => setValues((prev) => ({ ...prev, [f.name]: e.target.value }))}
                            className="w-full bg-surface border border-border rounded-lg px-2.5 py-1.5 text-sm text-content outline-none focus:border-brand-500" />
                    </div>
                ))}

                {transition.requiresDocument && (
                    <label className="block cursor-pointer">
                        <input type="file" accept="application/pdf,image/*" className="hidden"
                            onChange={(e) => {
                                const choisi = e.target.files?.[0] || null;
                                if (choisi && choisi.size > 10000000) {
                                    setFileError(`Ce document pèse ${(choisi.size / 1048576).toFixed(1)} Mo — au-delà de 10 Mo, la plupart des messageries refusent la pièce jointe et le client ne recevrait rien. Compressez-le puis réessayez.`);
                                    setFile(null);
                                    return;
                                }
                                setFileError('');
                                setFile(choisi);
                            }} />
                        <div className={`border-2 border-dashed rounded-xl p-5 flex flex-col items-center justify-center gap-1 transition-colors ${file ? 'border-brand-500/60 bg-brand-500/5' : 'border-border hover:border-brand-500/40 hover:bg-surface-soft'}`}>
                            {file ? (
                                <>
                                    <FileText size={22} className="text-brand-600 dark:text-brand-400" />
                                    <p className="text-xs font-bold text-content">{file.name}</p>
                                    <p className="text-[10px] text-content-muted">{(file.size / 1024).toFixed(0)} Ko</p>
                                </>
                            ) : (
                                <>
                                    <Upload size={22} className="text-content-muted" />
                                    <p className="text-xs font-bold text-content">Choisir le document</p>
                                    <p className="text-[10px] text-content-muted">Obligatoire pour cette étape — 10 Mo maximum</p>
                                </>
                            )}
                        </div>
                    </label>
                )}

                {etapePlaquette && <PlaquetteEmailFields valeur={courriel} onChange={setCourriel} destinataire={destinataire} destinataireEstReferent={destinataireEstReferent} />}
                {etapeDevis && <DevisEmailFields valeur={courrielDevis} onChange={setCourrielDevis} destinataire={destinataire} destinataireEstReferent={destinataireEstReferent} />}

                {demarcheManquante && !error && (
                    <p className="text-[11px] text-amber-600 dark:text-amber-400 font-semibold">
                        Choisissez la Démarche vers laquelle le bouton du mail renverra le client.
                    </p>
                )}
                {documentManquant && !error && (
                    <p className="text-[11px] text-amber-600 dark:text-amber-400 font-semibold">
                        Cette étape ne peut pas être validée sans son document.
                    </p>
                )}
                {fileError && <p className="text-xs text-red-500 font-semibold">{fileError}</p>}
                {error && <p className="text-xs text-red-500 font-semibold">{error}</p>}
            </div>
        </Modal>
    );
}
